import { useCallback, useContext, useRef, useState } from 'react';
import FirebaseContext from '../context/firebase';
import { REACT_APP_API_BASE_URL } from '../env';
import {
  AiChatMessage,
  AiStreamState,
  applyEvent,
  finalizeMessage,
  initialStreamState,
  parseSseChunk,
} from '../components/ai/sseProtocol';
import { AiAttachmentPayload } from '../components/ai/attachmentUtils';

// Port of HemIQ's useOrchidAiChat. The axios wrappers force responseType:
// 'json' and cannot stream, so this hook drives the SSE endpoint with fetch +
// ReadableStream directly. The frame/state logic lives in sseProtocol.ts
// (pure, unit-tested); this hook is the network/React shell around it.

type UseAiChatOptions = {
  // "all" | owner user id | null (regular users send null → own scope)
  scope?: string | null;
  conversationId: string;
  initialMessages?: AiChatMessage[];
};

export default function useAiChat({ scope, conversationId, initialMessages }: UseAiChatOptions) {
  const { firebaseApp } = useContext(FirebaseContext);
  const [messages, setMessages] = useState<AiChatMessage[]>(initialMessages ?? []);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [trace, setTrace] = useState<string[]>([]);
  const [partial, setPartial] = useState('');
  const [queuedMessages, setQueuedMessages] = useState<string[]>([]);

  // Mirrors for values read inside the async send flow.
  const messagesRef = useRef<AiChatMessage[]>(initialMessages ?? []);
  const loadingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const pushMessage = useCallback((message: AiChatMessage) => {
    messagesRef.current = [...messagesRef.current, message];
    setMessages(messagesRef.current);
  }, []);

  const runExchange = useCallback(
    async (text: string, attachments: AiAttachmentPayload[] = []) => {
      loadingRef.current = true;
      setLoading(true);
      setStatusText(null);

      // History is rebuilt server-side from persisted messages (never trusted
      // from the client), so only the new message + attachments are sent.
      pushMessage({
        role: 'user',
        content: text,
        attachments: attachments.length ? attachments.map((a) => ({ name: a.name })) : undefined,
      });

      const abort = new AbortController();
      abortRef.current = abort;
      setTrace([]);
      setPartial('');
      let state: AiStreamState = initialStreamState;

      try {
        const currentUser = firebaseApp.auth().currentUser;
        const idToken = currentUser ? await currentUser.getIdToken() : undefined;

        const response = await fetch(`${REACT_APP_API_BASE_URL}/ai/message`, {
          method: 'POST',
          credentials: 'include',
          signal: abort.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { 'x-access-token': idToken } : {}),
          },
          body: JSON.stringify({
            message: text,
            scope: scope ?? null,
            conversationId,
            attachments,
          }),
        });

        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('text/event-stream')) {
          // Pre-flight JSON error (422/403) or a non-streaming fallback body.
          const body = await response.json().catch(() => ({}));
          if (body.error) {
            state = applyEvent(state, { type: 'error', error: body.error });
          } else if (body.response !== undefined) {
            state = applyEvent(state, {
              type: 'done',
              response: body.response,
              truncated: body.truncated,
            });
          } else {
            state = applyEvent(state, { type: 'error', error: 'Something went wrong.' });
          }
        } else if (response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const parsed = parseSseChunk(buffer, decoder.decode(value, { stream: true }));
            buffer = parsed.rest;
            for (const event of parsed.events) {
              state = applyEvent(state, event);
              setStatusText(state.statusText);
              setTrace(state.trace);
              setPartial(state.partial);
            }
          }
        }

        if (!state.done) {
          state = applyEvent(state, {
            type: 'error',
            error: 'The connection was interrupted. Please try again.',
          });
        }
        pushMessage(finalizeMessage(state));
      } catch (error: any) {
        if (error && error.name === 'AbortError') {
          // Keep whatever streamed before the stop, if anything.
          pushMessage(
            state.partial
              ? { role: 'assistant', content: state.partial, stopped: true, queries: state.queries }
              : { role: 'assistant', content: 'Generation stopped.', stopped: true }
          );
        } else {
          pushMessage({ role: 'assistant', content: 'Something went wrong.', error: true });
        }
      } finally {
        abortRef.current = null;
        loadingRef.current = false;
        setLoading(false);
        setStatusText(null);
        setTrace([]);
        setPartial('');
        // Auto-fire the next queued message, if any.
        setQueuedMessages((queue) => {
          if (queue.length > 0) {
            const [next, ...rest] = queue;
            // Defer so state settles before the next exchange starts.
            setTimeout(() => runExchange(next), 0);
            return rest;
          }
          return queue;
        });
      }
    },
    [conversationId, firebaseApp, pushMessage, scope]
  );

  const sendMessage = useCallback(
    (text: string, attachments: AiAttachmentPayload[] = []) => {
      const trimmed = text.trim();
      if (!trimmed && attachments.length === 0) return;
      // Messages sent while a request is in flight are queued out of history
      // and auto-fired when the current exchange finishes (HemIQ parity).
      // Attachments are not queued — only immediate sends carry them.
      if (loadingRef.current) {
        if (trimmed) setQueuedMessages((queue) => [...queue, trimmed]);
        return;
      }
      runExchange(trimmed, attachments);
    },
    [runExchange]
  );

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clearQueuedMessage = useCallback((index: number) => {
    setQueuedMessages((queue) => queue.filter((_, i) => i !== index));
  }, []);

  return {
    messages,
    loading,
    statusText,
    trace,
    partial,
    queuedMessages,
    sendMessage,
    stopGeneration,
    clearQueuedMessage,
  };
}
